import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { chooseDefaultPaymentMethod, getStaffPaymentMethods } from "../api/paymentMethods";
import { refundWallet } from "../api/wallet";
import Button from "./common/Button";
import { formatCurrency } from "../utils/currency";
import { useRegionalPaymentDefaults } from "../context/RegionalSettingsContext";
import { TextInput, SelectInput, TextareaInput, CheckboxField } from "./common/form";
import { showConfirm } from "../utils/popup";
import { apiErrorMessage } from '../utils/apiError';

export default function WalletRefund({ studentId, maxAmount, onDone, onCancel }) {
    const { t } = useTranslation();
    const { defaultPaymentMethod } = useRegionalPaymentDefaults();
    const [amount, setAmount] = useState("");
    const [mode, setMode] = useState(defaultPaymentMethod);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [ref, setRef] = useState("");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [methodsLoadError, setMethodsLoadError] = useState(false);
    const [gatewayRefund, setGatewayRefund] = useState(false);

    const isGatewayReference = (value) =>
        typeof value === "string" && /^(RAZORPAY|STRIPE|PAYPAL):/i.test(value.trim());

    React.useEffect(() => {
        let active = true;
        getStaffPaymentMethods()
            .then((rows) => {
                if (!active) return;
                setPaymentMethods(Array.isArray(rows) ? rows : []);
                setMode((prev) => chooseDefaultPaymentMethod(rows, defaultPaymentMethod || prev || "CASH"));
                setMethodsLoadError(false);
            })
            .catch((err) => {
                console.error("Failed to load payment methods", err);
                if (active) setMethodsLoadError(true);
            });
        return () => {
            active = false;
        };
    }, [defaultPaymentMethod]);

    const doRefund = async () => {
        if (!studentId) {
            setError(t('walletStaff.missingStudentInfo'));
            return;
        }

        const a = Number(amount);
        if (!a || a <= 0) {
            setError(t('walletStaff.invalidAmountPositive'));
            return;
        }

        if (a > maxAmount) {
            setError(t('walletStaff.cannotRefundMore', { amount: formatCurrency(maxAmount) }));
            return;
        }

        if (!notes || notes.trim().length === 0) {
            setError(t('walletStaff.reasonRequired'));
            return;
        }

        const confirmed = await showConfirm(
            t('walletStaff.refundConfirm', {
                amount: formatCurrency(a),
                defaultValue: `Refund ${formatCurrency(a)} from this wallet? This cannot be undone from here.`,
            }),
        );
        if (!confirmed) return;

        setError("");
        setLoading(true);

        try {
            const payload = {
                studentId: Number(studentId),
                amount: a,
                paymentMode: mode,
                reference: ref,
                notes: notes.trim(),
                refundedBy: null,
                gatewayRefund: gatewayRefund && isGatewayReference(ref),
            };

            await refundWallet(payload);
            alert(t('walletStaff.refundSuccess', { amount: formatCurrency(a) }));
            if (onDone) onDone();
        } catch (e) {
            console.error("refund failed", e);
            const errorMsg = apiErrorMessage(e, t('walletStaff.refundFailed'));
            setError(errorMsg);
            alert(`${t('walletStaff.refundFailed')}: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 border-2 border-red-300 rounded-lg space-y-3 bg-red-50">
            <div className="flex justify-between items-center">
                <div className="text-sm font-semibold text-red-700">{t('walletStaff.refundTitle')}</div>
                {onCancel && (
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700 text-xl"
                        title={t('common.cancel')}
                        type="button"
                    >
                        ×
                    </button>
                )}
            </div>

            <div className="text-xs text-red-600 bg-red-100 p-2 rounded">
                <strong>⚠️ {t('walletStaff.refundImportant')}</strong> {t('walletStaff.refundWarning')}{' '}
                <strong>{formatCurrency(maxAmount)}</strong>
            </div>

            {error && (
                <div className="text-sm text-red-600 bg-red-100 p-2 rounded border border-red-300">
                    {error}
                </div>
            )}
            {methodsLoadError && (
                <div className="text-xs text-rose-700 bg-rose-50 p-2 rounded border border-rose-200" role="status">
                    {t('walletStaff.paymentMethodsLoadFailed', { defaultValue: 'Could not load payment methods — using a fallback option.' })}
                </div>
            )}

            <TextInput
                label={t('walletStaff.refundAmount')}
                required
                type="number"
                placeholder={t('walletStaff.maxPlaceholder', { amount: formatCurrency(maxAmount) })}
                value={amount}
                onChange={e => {
                    setAmount(e.target.value);
                    setError("");
                }}
                max={maxAmount}
                step="0.01"
            />

            <SelectInput
                label={t('walletStaff.paymentMode')}
                required
                value={mode}
                onChange={e => setMode(e.target.value)}
            >
                {(paymentMethods.length ? paymentMethods : [{
                    methodKey: mode || 'CASH',
                    displayName: t('pages.wallet.methodCash', { defaultValue: 'Cash' }),
                }]).map((method) => (
                    <option key={method.methodKey} value={method.methodKey}>{method.displayName || method.methodKey}</option>
                ))}
            </SelectInput>

            <div>
                <TextInput
                    label={t('walletStaff.referenceTxn')}
                    placeholder={t('walletStaff.referencePlaceholder')}
                    value={ref}
                    onChange={e => {
                        setRef(e.target.value);
                        setGatewayRefund(isGatewayReference(e.target.value));
                    }}
                />
                {isGatewayReference(ref) && (
                    <CheckboxField
                        className="mt-2"
                        checked={gatewayRefund}
                        onChange={(e) => setGatewayRefund(e.target.checked)}
                        label={t('walletStaff.gatewayRefund')}
                    />
                )}
            </div>

            <TextareaInput
                label={t('walletStaff.reasonForRefund')}
                required
                placeholder={t('walletStaff.reasonPlaceholder')}
                value={notes}
                onChange={e => {
                    setNotes(e.target.value);
                    setError("");
                }}
                rows={3}
            />

            <div className="flex gap-2">
                {onCancel && (
                    <Button variant="secondary" onClick={onCancel}>{t('common.cancel')}</Button>
                )}
                <Button
                    variant="secondary"
                    onClick={() => {
                        setAmount("");
                        setRef("");
                        setNotes("");
                        setError("");
                    }}
                >
                    {t('walletStaff.clear')}
                </Button>
                <Button
                    variant="danger"
                    onClick={doRefund}
                    disabled={loading}
                >
                    {loading ? t('walletStaff.processing') : t('walletStaff.processRefund')}
                </Button>
            </div>
        </div>
    );
}
